/* eslint-disable no-underscore-dangle */
import EventEmitter from 'events';
import Immutable from 'immutable';
import { arrayMoveImmutable } from 'array-move';
import BASEMAPS, { ESRI_GOOGLE_TILE_TYPE } from '../layer-picker/basemaps';
import request from '../api/browser';
import fetchTilesWithAuth from '../services/tiles-auth-request';

const parseTileJSON = (body: string) => {
  try {
    return JSON.parse(body);
  } catch (error) {
    console.error('Failed to parse tile JSON response', error);
    return null;
  }
};

export type LayerTypes = 'xyz' | 'mbtiles' | 'geojson' | 'data' | 'fulcrum' | 'wms' | 'feature-service' | 'kml' | 'mmpk';
export type OriginTypes = 'fulcrum' | 'arcgis';

export type Layer = {
  id: string;
  name: string;
  type: LayerTypes;
  description: string|null;
  form?: string;
  origin?: OriginTypes;
  lastUpdated?: number|null;
  filter?: string;
  baseURL?: string;
  layerID?: string;
  url?: string;
  esriApiKey?: string;
  esriAppId?: string;
  esriPortalUrl?: string;
  esriUsername?: string;
  options?: {};
  data?: { json?: any, tileJSON?: any };
  geojson?: any;
  fileSize?: number;
}
export default class LayerSettings extends EventEmitter {
  _preferences;
  tileURL;
  layerURL;
  _formID;
  _activeBasemap;
  _googleEngineActiveBasemap;
  _activeOverlays: Layer[];
  _activeLayersImmutable: Layer[]|null;
  _activeSQLFilter;
  _layerList;
  _dataLayer?: Layer;
  arcgisLayerList: Layer[];
  _lastUpdated;

  constructor(layerList, formID, tileURL, layerURL, preferences) {
    super();

    this._preferences = preferences;

    this.tileURL = tileURL;
    this.layerURL = layerURL;

    this._formID = formID;

    // eslint-disable-next-line prefer-destructuring
    this._activeOverlays = [];
    this._activeLayersImmutable = null;

    this._activeSQLFilter = null;

    this._layerList = layerList.map((o) => this.createLayer(o)).filter((o) => o);
    this.arcgisLayerList = [];

    this.loadSavedOverlays();
  }

  get preferences() {
    return this._preferences;
  }

  get googleEngineActiveBasemap() {
    // Google engine basemap
    if (this._googleEngineActiveBasemap) {
      return this._googleEngineActiveBasemap;
    }
    const googleBasemap = BASEMAPS.find((map) => map.id === this.preferences.lastMap);
    if (!googleBasemap || googleBasemap?.type === ESRI_GOOGLE_TILE_TYPE) {
      return BASEMAPS[0];
    }
    return googleBasemap;
  }

  get activeBasemap() {
    if (this._activeBasemap) {
      return this._activeBasemap;
    }

    const lastBasemap = this.syncBasemapPreference();

    return lastBasemap || this.defaultBasemap();
  }

  syncBasemapPreference() {
    let lastBasemap = this.basemapById(this.preferences.lastBasemap);
    const shouldAdoptGoogleTiles = !this.preferences.basemapTilesHaveBeenReset;

    if (shouldAdoptGoogleTiles) {
      this.preferences.lastBasemap = BASEMAPS[4].id;
      this.preferences.basemapTilesHaveBeenReset = true;
      lastBasemap = this.basemapById(this.preferences.lastBasemap);
    }

    return lastBasemap;
  }

  defaultBasemap() {
    return BASEMAPS[4];
  }

  basemapById(id?: string) {
    return BASEMAPS.find((map) => map.id === id);
  }

  get activeOverlays() {
    return this._activeOverlays;
  }

  get dataLayer(): Layer | undefined {
    return this._dataLayer;
  }

  get lastUpdated() {
    return this._lastUpdated;
  }

  saveLayers() {
    this.preferences.lastMapLayers = this._activeOverlays.map((o) => ({ id: o.id }));
    this.emit('layers-saved');
  }

  refresh() {
    this._lastUpdated = new Date().getTime();
    this.emit('change');
  }

  invalidateDataLayer() {
    if (this.dataLayer) {
      this.dataLayer.lastUpdated = new Date().getTime();
    }
  }

  updateTileSQL(sql) {
    if (this.dataLayer && this.dataLayer.filter !== sql) {
      this.dataLayer.filter = sql;
      this.refresh();
    }
  }

  moveOverlay(fromIndex, toIndex) {
    this._activeOverlays = arrayMoveImmutable(this._activeOverlays, fromIndex, toIndex);
    this._activeLayersImmutable = null;

    this.saveLayers();

    this.refresh();
  }

  updateBasemap(basemap, esriEnabled: boolean) {
    this._activeLayersImmutable = null;

    if (esriEnabled) {
      this._activeBasemap = basemap;
      this.preferences.lastBasemap = basemap.id;
      this.refresh();
      return;
    }

    this._googleEngineActiveBasemap = basemap;
    this.preferences.lastMap = basemap.id;
    this.refresh();
  }

  isOverlayActive(overlay) {
    return this._activeOverlays.map((o) => o.id).indexOf(overlay.id) !== -1;
  }

  activeOverlayIds(): string[] {
    return this._activeOverlays.map((overlay) => overlay.id);
  }

  toggleOverlay(overlay: Layer) {
    const index = this.activeOverlayIds().indexOf(overlay.id);

    if (index > -1) {
      this._activeOverlays.splice(index, 1);
    } else {
      this._activeOverlays.push(overlay);
    }

    this.saveLayers();

    this.refresh();
  }

  updateOverlays(overlays) {
    this._activeOverlays = overlays || [];
    this._activeLayersImmutable = null;

    this.saveLayers();

    this.refresh();
  }

  get activeLayersImmutable(): Immutable.List<Immutable.Map<any, any>> {
    const layers: Layer[] = Array.from(this.activeOverlays) || [];
    if (this.dataLayer) {
      layers.push(this.dataLayer);
    }
    return Immutable.List(layers.map((o) => Immutable.Map(o)));
  }

  get layerList() {
    return [...this.arcgisLayerList, ...this._layerList];
  }

  loadSavedOverlays() {
    const formID = this._formID;

    const overlays = this.preferences.lastMapLayers.map((map) => {
      const validMap = this.layerList?.find((layer) => layer.id === map.id);

      // filter out any map that's in a different account or is gone
      if (!validMap) {
        return null;
      }

      return validMap;
      // return this.createLayer(map);
    }).filter((map) => map != null);

    if (formID) {
      this._dataLayer = {
        id: 'fulcrum-data',
        name: 'Fulcrum Data',
        type: 'data',
        form: formID,
        lastUpdated: null,
        filter: '',
        baseURL: this.tileURL,
        description: '',
      };
    }

    this._activeOverlays = overlays;
    this._activeLayersImmutable = null;
  }

  createLayer(layer): Layer|null {
    const attrs = {
      id: layer.id,
      name: layer.name,
      origin: layer.origin,
      description: layer.description,
    };

    switch (layer.type) {
      case 'mbtiles': {
        return {
          ...attrs,
          type: 'mbtiles',
          layerID: layer.id,
          baseURL: this.layerURL,
          fileSize: layer.file_size,
        };
      }
      case 'kml': {
        return {
          ...attrs,
          type: 'kml',
          layerID: layer.id,
          url: layer.source,
          fileSize: layer.file_size,
        };
      }

      case 'mmpk': {
        // MMPK layers are mobile-only and intentionally excluded from web map rendering.
        return null;
      }

      case 'geojson': {
        const result: Layer = {
          ...attrs,
          type: 'geojson',
        };

        if (layer.source.substr(0, 4) === 'http') {
          result.url = layer.source;
        } else {
          try {
            result.geojson = JSON.parse(layer.source);
          } catch (ex) {
            // invalid geojson
          }
        }

        return result;
      }

      case 'feature-service': {
        return {
          ...attrs,
          type: 'feature-service',
          filter: '',
          url: layer.source,
        };
      }

      case 'fulcrum': {
        return {
          ...attrs,
          type: 'fulcrum',
          form: layer.source,
          filter: '',
          baseURL: this.tileURL,
        };
      }

      case 'xyz': {
        return {
          ...attrs,
          type: 'xyz',
          url: layer.source,
          options: { maxZoom: 22 },
        };
      }

      case 'wms': {
        return {
          ...attrs,
          type: 'wms',
          url: layer.source,
          options: { maxZoom: 22 },
        };
      }

      default: break;
    }

    return null;
  }

  fetchGeoJSON(layer, callback) {
    const theLayer = this.activeOverlays.find((o) => o.id === layer.id);

    if (!theLayer) {
      return;
    }

    theLayer.data = { json: null };
    // @ts-ignore
    globalThis.$.getJSON(theLayer.url, (json) => {
      if (theLayer.data) {
        theLayer.data.json = json;
      }

      this.refresh();

      if (callback) {
        return callback();
      }

      return null;
    });
  }

  fetchTileJsonData(layer): Promise<Object> {
    return new Promise((resolve, reject) => {
      const theLayer = this.activeOverlays.find((o) => o.id === layer.id);

      if (theLayer) {
        const tileJSONURL = `${this.layerURL}/tile/mbtiles/jwt/template/${theLayer.layerID}.json`;
        fetchTilesWithAuth(tileJSONURL, { credentials: 'omit' })
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to fetch tile json: ${response.status}`);
            }

            return response.text();
          })
          .then((body) => {
            try {
              resolve(JSON.parse(body));
            } catch (error) {
              reject(error);
            }
          })
          .catch((error) => reject(error));
      } else {
        reject(new Error(`Layer ${layer.id} is not active`));
      }
    });
  }

  fetchTileJSON(layer, token) {
    const theLayer = this.activeOverlays.find((o) => o.id === layer.id);

    if (!theLayer) {
      return;
    }

    theLayer.data = { tileJSON: null };

    // Support both token-in-URL (Google Maps) and JWT-based (Esri) authentication
    // If token is provided, use it for URL-based auth (Google Maps)
    // Otherwise, use JWT auth (Esri) via fetchTilesWithAuth
    if (token) {
      const tileJSONURL = `${this.layerURL}/tile/mbtiles/template/${token}/${theLayer.layerID}.json`;
      request({ url: tileJSONURL, withCredentials: false }, (err, res, body) => {
        if (err) return;
        const tileJSON = parseTileJSON(body);
        if (tileJSON && theLayer.data) {
          theLayer.data.tileJSON = tileJSON;
          this.refresh();
        }
      });
    } else {
      // Esri/JWT path
      const tileJSONURL = `${this.layerURL}/tile/mbtiles/jwt/template/${theLayer.layerID}.json`;
      fetchTilesWithAuth(tileJSONURL, { credentials: 'omit' })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch tile json: ${response.status}`);
          }
          return response.text();
        })
        .then((body) => {
          const tileJSON = parseTileJSON(body);
          if (tileJSON && theLayer.data) {
            theLayer.data.tileJSON = tileJSON;
            this.refresh();
          }
        })
        .catch((error) => {
          // Silently degrade if JWT token fetch fails
        });
    }
  }
}
